import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  CheckCircle2, 
  Clock, 
  PenLine,
  RotateCcw,
  Download
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicSignPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedAssoc = searchParams.get('association');
  
  const [constitution, setConstitution] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [selectedAssociation, setSelectedAssociation] = useState(preselectedAssoc || '');
  const [presidentName, setPresidentName] = useState('');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const sigCanvas = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [constRes, assocRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/constitution`),
        axios.get(`${API_URL}/associations`),
        axios.get(`${API_URL}/signatures/status`)
      ]);
      setConstitution(constRes.data);
      setAssociations(assocRes.data);
      setSignatureStatus(statusRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSign = async () => {
    if (!selectedAssociation) {
      toast.error('Please select your association');
      return;
    }
    if (!presidentName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error('Please provide your signature');
      return;
    }
    
    // Check if already signed
    const assoc = associations.find(a => a.id === selectedAssociation);
    if (assoc?.signature_status === 'signed') {
      toast.error('This association has already signed the constitution');
      return;
    }
    
    setSigning(true);
    try {
      // Get signature data - use regular canvas if trimmed is empty
      let signatureData;
      try {
        const trimmedCanvas = sigCanvas.current.getTrimmedCanvas();
        signatureData = trimmedCanvas.toDataURL('image/png');
        
        // Fallback to full canvas if trimmed is too small
        if (!signatureData || signatureData.length < 100) {
          signatureData = sigCanvas.current.toDataURL('image/png');
        }
      } catch (canvasError) {
        signatureData = sigCanvas.current.toDataURL('image/png');
      }
      
      if (!signatureData || signatureData === 'data:,') {
        toast.error('Could not capture signature. Please try drawing again.');
        setSigning(false);
        return;
      }
      
      const response = await axios.post(`${API_URL}/public/sign`, {
        constitution_id: constitution.id,
        association_id: selectedAssociation,
        president_name: presidentName.trim(),
        signature_data: signatureData
      });
      
      // Get association name for confirmation page
      const assoc = associations.find(a => a.id === selectedAssociation);
      const associationName = assoc ? `${assoc.short_name} - ${assoc.name}` : '';
      
      toast.success('Constitution signed successfully!');
      
      // Redirect to confirmation page with state
      navigate('/sign/confirmation', {
        state: {
          associationName: associationName,
          presidentName: presidentName.trim()
        }
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to sign constitution';
      toast.error(errorMessage);
    } finally {
      setSigning(false);
    }
  };

  const renderContent = (content) => {
    return content?.split('\n').map((line, index) => {
      if (line.startsWith('# ')) {
        return <h1 key={index} className="text-2xl font-serif font-bold text-cameroon-green mt-8 mb-4">{line.slice(2)}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-serif font-semibold text-cameroon-red mt-6 mb-3">{line.slice(3)}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={index} className="text-lg font-semibold mt-4 mb-2">{line.slice(4)}</h3>;
      } else if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="font-semibold mt-3 mb-2">{line.slice(2, -2)}</p>;
      } else if (line.startsWith('- ')) {
        return <li key={index} className="ml-6 mb-1 list-disc">{line.slice(2)}</li>;
      } else if (line === '---') {
        return <hr key={index} className="my-6 border-border" />;
      } else if (line.trim()) {
        return <p key={index} className="mb-3 leading-relaxed">{line}</p>;
      }
      return null;
    });
  };

  const pendingAssociations = associations.filter(a => a.signature_status !== 'signed');
  const isCompiled = signatureStatus?.is_compiled || false;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cameroon-green"></div>
      </div>
    );
  }

  // If constitution is compiled, show a message
  if (isCompiled) {
    return (
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                  <span className="text-white font-serif font-bold text-lg">C</span>
                </div>
                <span className="font-serif font-bold text-white text-xl">CCA Montreal</span>
              </Link>
              <Link to="/" className="text-white/60 hover:text-white text-sm flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="glass border-white/10">
            <CardContent className="p-8 text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-cameroon-green/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-cameroon-green" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-bold text-white mb-2">
                  Constitution Compiled
                </h1>
                <p className="text-white/60">
                  The CCA Constitution has been officially compiled and is no longer accepting signatures.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20">
                <p className="text-white/80 text-sm">
                  <strong>{signatureStatus?.signed || 0}</strong> associations have signed the constitution.
                  The document was compiled on{' '}
                  <strong>{signatureStatus?.compiled_at ? new Date(signatureStatus.compiled_at).toLocaleDateString() : 'N/A'}</strong>.
                </p>
              </div>
              {signatureStatus?.public_download_enabled && (
                <Button 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API_URL}/constitution/pdf/public`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'CCA_Constitution_Signed.pdf');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      toast.error('Unable to download PDF');
                    }
                  }}
                  className="bg-cameroon-green hover:bg-cameroon-green/90"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Constitution PDF
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/')} className="border-white/20 text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                <span className="text-white font-serif font-bold text-lg">C</span>
              </div>
              <span className="font-serif font-bold text-white text-xl">CCA Montreal</span>
            </Link>
            <Link to="/" className="text-white/60 hover:text-white text-sm flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 text-center">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2">
            Sign the CCA Constitution
          </h1>
          <p className="text-white/60">
            Association presidents: Select your association, read the constitution, and sign below
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Constitution Content */}
          <div className="lg:col-span-2">
            <Card className="bg-white">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-serif text-xl">{constitution?.title}</CardTitle>
                    <CardDescription>Version {constitution?.version}</CardDescription>
                  </div>
                  <Tabs value={language} onValueChange={setLanguage}>
                    <TabsList>
                      <TabsTrigger value="en">English</TabsTrigger>
                      <TabsTrigger value="fr">Français</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ScrollArea className="h-[60vh]">
                  <div className="prose prose-sm max-w-none pr-4">
                    {language === 'en' 
                      ? renderContent(constitution?.content)
                      : renderContent(constitution?.content_fr)
                    }
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Signing Panel */}
          <div className="space-y-6">
            {/* Progress */}
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-cameroon-green" />
                  <span className="font-semibold text-white">Signature Progress</span>
                </div>
                <div className="text-center py-4">
                  <div className="text-4xl font-bold text-cameroon-green">{signatureStatus?.signed || 0}</div>
                  <div className="text-sm text-white/60">of {signatureStatus?.total || 12} signed</div>
                </div>
                
                {/* Signed List */}
                <div className="space-y-3 mt-4">
                  <div className="text-xs text-white/40 uppercase tracking-wide flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-cameroon-green" />
                    Signed
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {signatureStatus?.signed_associations?.map((a) => (
                      <Badge key={a.association_id} className="bg-cameroon-green/20 text-cameroon-green text-xs">
                        {a.short_name}
                      </Badge>
                    ))}
                    {signatureStatus?.signed_associations?.length === 0 && (
                      <span className="text-xs text-white/40">None yet</span>
                    )}
                  </div>
                  
                  <div className="text-xs text-white/40 uppercase tracking-wide flex items-center gap-2 mt-3">
                    <Clock className="w-3 h-3 text-cameroon-yellow" />
                    Pending
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {signatureStatus?.pending_associations?.map((a) => (
                      <Badge key={a.association_id} variant="outline" className="text-white/60 text-xs border-white/20">
                        {a.short_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Signing Form */}
            <Card className="glass border-white/10">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-white flex items-center gap-2">
                  <PenLine className="w-5 h-5 text-cameroon-yellow" />
                  Sign Constitution
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/80">Select Your Association</Label>
                  <Select value={selectedAssociation} onValueChange={setSelectedAssociation}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-association">
                      <SelectValue placeholder="Choose association..." />
                    </SelectTrigger>
                    <SelectContent>
                      {pendingAssociations.map((assoc) => (
                        <SelectItem key={assoc.id} value={assoc.id}>
                          {assoc.short_name} - {assoc.name}
                        </SelectItem>
                      ))}
                      {pendingAssociations.length === 0 && (
                        <SelectItem value="none" disabled>All associations have signed</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80">President's Full Name</Label>
                  <Input
                    value={presidentName}
                    onChange={(e) => setPresidentName(e.target.value)}
                    placeholder="Enter your full name"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    data-testid="president-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80">Your Signature</Label>
                  <div className="border-2 border-dashed border-white/20 rounded-lg overflow-hidden bg-white">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-40',
                        'data-testid': 'signature-canvas'
                      }}
                      backgroundColor="white"
                      penColor="#005C46"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearSignature}
                    className="text-white/60 hover:text-white"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                </div>

                <Button
                  onClick={handleSign}
                  disabled={signing || pendingAssociations.length === 0}
                  className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
                  data-testid="submit-signature"
                >
                  {signing ? 'Signing...' : 'Submit Signature'}
                </Button>

                <p className="text-xs text-white/40 text-center">
                  By signing, I confirm I am the authorized president of the selected association and agree to the terms of the CCA Constitution.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PublicSignPage;
